import { Plus } from 'lucide-react'
import { createPortal } from 'react-dom'
import { ActionMenu, type Action, type ActionGroup } from '@/components/ActionMenu'
import { useFrame } from '@/lib/frame'
import { usePosts } from '@/lib/sections'
import type { UseSection } from '../../shared/uses'

const plus =
  'grid size-11 shrink-0 place-items-center rounded-xl text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:opacity-60 android:size-12 android:rounded-full android:active:bg-secondary android:active:opacity-100 nav:size-10 nav:rounded-full nav:transition-colors nav:hover:bg-secondary nav:active:opacity-100'

// Every place makes new things the same way: a plain "+" at the right end of the top bar (the phone's in
// nav/PhoneShell.tsx, a tablet's or computer's in nav/Sidebar.tsx). One action happens straight away; two or more
// open a sheet on a phone and a menu elsewhere, never a row of buttons. One per screen, and never floating over the
// page: an empty page's own invitation ("Write the first moment") is the first-time way in, and this the lasting one.
// A section's "+" isn't there for someone who has picked Just see for it (What I use).
// `groups` sets some actions apart from the rest (People's ways to connect, above making a circle).
export function CreateAction({
  title,
  actions = [],
  groups = [actions],
  section,
}: {
  title: string
  actions?: Action[]
  groups?: ActionGroup[]
  section?: UseSection
}) {
  const { actionSlot } = useFrame()
  const posting = usePosts(section)
  const all = groups.flatMap((g) => (Array.isArray(g) ? g : g.actions))
  if (!actionSlot || all.length === 0 || !posting) return null
  const only = all.length === 1 ? all[0]! : null
  const label = only ? only.label : title
  const button = (
    <button type="button" aria-label={label} title={label} onClick={only?.onSelect} className={plus}>
      <Plus className="size-6.5 stroke-[1.75]" aria-hidden />
    </button>
  )
  return createPortal(
    only ? button : <ActionMenu title={title} trigger={button} groups={groups} align="end" />,
    actionSlot,
  )
}
